import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import type { paths } from '../schema';
import type { CustomObjectField } from './custom-object';
import type { RequestOptions, RequestOptionsWithParams } from './types';
import { curry1 } from './utils';

export type SearchFieldsOptions = RequestOptionsWithParams<
  paths['/api/client/fields/search']['post']['parameters']['query']
> & {
  body?: paths['/api/client/fields/search']['post']['requestBody']['content']['application/json'];
};

export type SearchAllFieldsParams =
  paths['/api/client/fields/settings-search']['get']['parameters']['query'];

export type GetClientCategoryParams =
  paths['/api/client/categories']['get']['parameters']['query'];

export type ClientCategory =
  paths['/api/client/categories']['get']['responses']['200']['content']['application/json'][number];

type ClientFieldRequest =
  paths['/api/client/fields']['post']['requestBody']['content']['application/json'];
type ClientObject =
  paths['/api/client/custom-object']['get']['responses']['200']['content']['application/json'];


export const createClientField = async (
  instance: AxiosInstance,
  data: ClientFieldRequest,
  options?: RequestOptions
): Promise<CustomObjectField> => {
  const res = await instance.post<CustomObjectField>(
    '/client/fields',
    data,
    options
  );

  return res.data;
};

export const getClientCategories = async (
  instance: AxiosInstance,
  options?: RequestOptionsWithParams<GetClientCategoryParams>
): Promise<ClientCategory[]> => {
  const res = await instance.get<ClientCategory[]>(
    '/client/categories',
    options
  );

  return res.data;
};

/**
 * @remarks this bypasses permissions to return all fields
 */
export const getAllClientFields = async (
  instance: AxiosInstance,
  options?: RequestOptionsWithParams<SearchAllFieldsParams>
): Promise<CustomObjectField[]> => {
  const res = await instance.get<CustomObjectField[]>(
    '/client/fields/settings-search',
    options
  );

  return res.data;
};

export const getClientFields = async (
  instance: AxiosInstance,
  options: SearchFieldsOptions = {}
): Promise<CustomObjectField[]> => {
  const { body, params, ...opts } = options;

  const config: AxiosRequestConfig = { ...opts, params };

  const res = await instance.post<CustomObjectField[]>(
    '/client/fields/search',
    body,
    config
  );

  return res.data;
};

export const getClientObject = async (
  instance: AxiosInstance,
  options?: RequestOptions
): Promise<ClientObject> => {
  const res = await instance.get<ClientObject>(
    '/client/custom-object',
    options
  );

  return res.data;
};

export class ClientService {
  public createClientField;
  public getAllClientFields;
  public getClientCategories;
  public getClientFields;
  public getClientObject;

  constructor(private instance: AxiosInstance) {
    this.createClientField = curry1(createClientField)(this.instance);
    this.getAllClientFields = curry1(getAllClientFields)(this.instance);
    this.getClientCategories = curry1(getClientCategories)(this.instance);
    this.getClientFields = curry1(getClientFields)(this.instance);
    this.getClientObject = curry1(getClientObject)(this.instance);
  }
}
